import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

interface Client {
  _id: string;
  clientCode: string;
  dbName: string;
  phoneNoId: string;
  wabaId: string;
  appId: string;
  isActive: boolean;
  displayPhoneNumber?: string;
  verifiedName?: string;
  onboardedAt?: string;
}

type OnboardingStep = 'form' | 'meta-pending' | 'confirm' | 'done';

@Component({
  selector: 'app-client-onboarding',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './client-onboarding.component.html',
  styles: [],
})
export class ClientOnboardingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('addClientModal') addClientModalRef!: ElementRef<HTMLElement>;

  clients: Client[] = [];
  isLoading = false;
  errorMessage = '';

  // Onboarding form state
  newClientCode = '';
  newDbName = '';
  onboardingStep: OnboardingStep = 'form';
  capturedWabaId = '';
  capturedPhoneNoId = '';
  authCode = '';
  confirmedPhone = '';
  confirmedName = '';
  isSaving = false;
  formError = '';

  // FB SDK state — tracked separately so each can arrive in any order
  fbInitialized = false;
  private metaAppId = '';
  private metaConfigId = '';
  private fbSdkLoaded = false;       // true after fbAsyncInit fires
  private messageHandler: ((e: MessageEvent) => void) | null = null;

  constructor(private apiService: ApiRequestsService) {}

  ngOnInit(): void {
    // Start SDK load and config fetch in parallel — FB.init() fires once both arrive
    this.startFbSdkLoad();
    this.loadConfig();
    this.loadClients();
  }

  ngAfterViewInit(): void {
    const modalEl = this.addClientModalRef?.nativeElement;
    if (modalEl) document.body.appendChild(modalEl);
  }

  ngOnDestroy(): void {
    const modalEl = this.addClientModalRef?.nativeElement;
    if (modalEl?.parentNode === document.body) document.body.removeChild(modalEl);
    if (this.messageHandler) window.removeEventListener('message', this.messageHandler);
  }

  // ── SDK loading ────────────────────────────────────────────────────────────

  private startFbSdkLoad(): void {
    if ((window as any).FB) {
      // SDK already present (cached from prior navigation); mark loaded and try init
      this.fbSdkLoaded = true;
      this.tryInitFb();
      return;
    }

    // fbAsyncInit is called by the SDK as soon as it finishes executing
    const self = this;
    (window as any).fbAsyncInit = function () {
      self.fbSdkLoaded = true;
      self.tryInitFb();
    };

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }

  // Called whenever either SDK-loaded or config-arrived state changes.
  // Calls FB.init() only once both are ready.
  private tryInitFb(): void {
    if (!this.fbSdkLoaded || !this.metaAppId) return;
    if (this.fbInitialized) return;
    (window as any).FB.init({ appId: this.metaAppId, autoLogAppEvents: true, xfbml: true, version: 'v21.0' });
    this.fbInitialized = true;
  }

  // ── Config / data ──────────────────────────────────────────────────────────

  loadConfig(): void {
    this.apiService.getApiCall('api/v1/client-onboarding/config').subscribe({
      next: (res: any) => {
        if (res?.errorCode === this.apiService.NO_ERROR) {
          this.metaAppId = res.Data?.appId || '';
          this.metaConfigId = res.Data?.configId || '';
          this.tryInitFb();   // SDK may already be loaded; init now if so
        }
      },
      error: () => {},
    });
  }

  loadClients(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.apiService.getApiCall('api/v1/client-onboarding/list').subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res?.errorCode === this.apiService.NO_ERROR) {
          this.clients = res.Data || [];
        } else {
          this.errorMessage = res?.errorDescription || 'Unable to load clients.';
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Unable to load clients.';
      },
    });
  }

  // ── Modal ──────────────────────────────────────────────────────────────────

  openAddModal(): void {
    this.resetForm();
    const modalEl = this.addClientModalRef?.nativeElement;
    if (!modalEl) return;
    const modalInstance = bootstrap?.Modal?.getInstance(modalEl) || new bootstrap.Modal(modalEl);
    modalInstance.show();
    this.registerMessageListener();
  }

  private registerMessageListener(): void {
    if (this.messageHandler) window.removeEventListener('message', this.messageHandler);
    this.messageHandler = (event: MessageEvent) => {
      if (!event.origin.includes('facebook.com')) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'WA_EMBEDDED_SIGNUP' && data?.event === 'FINISH') {
          this.capturedPhoneNoId = data.data?.phone_number_id || '';
          this.capturedWabaId = data.data?.waba_id || '';
        }
      } catch { /* non-JSON messages ignored */ }
    };
    window.addEventListener('message', this.messageHandler);
  }

  // ── Meta Embedded Signup ───────────────────────────────────────────────────

  launchMetaSignup(): void {
    if (window.location.protocol !== 'https:') {
      this.formError = 'Meta Embedded Signup requires HTTPS. For local development run: ng serve --ssl';
      return;
    }
    if (!this.fbInitialized) {
      this.formError = 'Meta SDK is still loading — please wait a moment and try again.';
      return;
    }
    if (!this.newClientCode.trim() || !this.newDbName.trim()) {
      this.formError = 'Please fill in Client Code and Database Name before connecting.';
      return;
    }

    this.formError = '';
    this.capturedWabaId = '';
    this.capturedPhoneNoId = '';
    this.authCode = '';
    this.onboardingStep = 'meta-pending';

    (window as any).FB.login(
      (response: any) => {
        if (response.authResponse?.code) {
          this.authCode = response.authResponse.code;
          this.onboardingStep = 'confirm';
        } else {
          this.onboardingStep = 'form';
          this.formError = 'Meta authorization was cancelled or failed. Please try again.';
        }
      },
      {
        config_id: this.metaConfigId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { featureName: 'whatsapp_embedded_signup', sessionInfoVersion: 3 },
      }
    );
  }

  async confirmAndSave(): Promise<void> {
    this.isSaving = true;
    this.formError = '';
    try {
      const res = await this.apiService.postApiCallReturningPromise('api/v1/client-onboarding/complete', {
        code: this.authCode,
        clientCode: this.newClientCode.trim(),
        dbName: this.newDbName.trim(),
        wabaId: this.capturedWabaId.trim(),
        phoneNoId: this.capturedPhoneNoId.trim(),
      });
      if (res?.errorCode === this.apiService.NO_ERROR) {
        this.confirmedPhone = res.Data?.displayPhoneNumber || this.capturedPhoneNoId;
        this.confirmedName = res.Data?.verifiedName || '';
        this.onboardingStep = 'done';
        this.loadClients();
      } else {
        this.formError = res?.errorDescription || 'Onboarding failed. Please try again.';
        this.onboardingStep = 'confirm';
      }
    } catch {
      this.formError = 'Onboarding failed. Please try again.';
      this.onboardingStep = 'confirm';
    } finally {
      this.isSaving = false;
    }
  }

  closeModal(): void {
    const modalEl = this.addClientModalRef?.nativeElement;
    if (!modalEl) return;
    bootstrap?.Modal?.getInstance(modalEl)?.hide();
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
  }

  async toggleClientStatus(client: Client): Promise<void> {
    try {
      const res = await this.apiService.postApiCallReturningPromise('api/v1/client-onboarding/update-status', {
        clientCode: client.clientCode,
        isActive: !client.isActive,
      });
      if (res?.errorCode === this.apiService.NO_ERROR) {
        client.isActive = !client.isActive;
      } else {
        this.errorMessage = res?.errorDescription || 'Failed to update client status.';
      }
    } catch {
      this.errorMessage = 'Failed to update client status.';
    }
  }

  private resetForm(): void {
    this.newClientCode = '';
    this.newDbName = '';
    this.onboardingStep = 'form';
    this.capturedWabaId = '';
    this.capturedPhoneNoId = '';
    this.authCode = '';
    this.confirmedPhone = '';
    this.confirmedName = '';
    this.formError = '';
    this.isSaving = false;
  }
}
